import { ShieldCheck } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import DisplayTooltipOnHover from 'components/reusable/DisplayTooltipOnHover'
import { Button, Modal } from 'slab'
import { postAdminComplianceChecksCheckIdOverrideFailingCheck } from 'utils/web_api'
import { type AdminComplianceCheckState } from 'utils/web_api.schemas'
import { useWindowLocation } from 'utils/windowLocation'

interface OverrideEmployerCheckStatusButtonProps {
  state: AdminComplianceCheckState
  checkId: string
  provider: string
  onSuccess?: () => void
}

function OverrideEmployerCheckStatusButton ({
  state,
  checkId,
  provider,
}: OverrideEmployerCheckStatusButtonProps) {
  const { reload } = useWindowLocation()

  // Only render if the check is in a failed state
  if (state !== 'failed') {
    return null
  }
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [reason, setReason] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const handleSubmit = async () => {
    if (!reason.trim()) return

    setIsLoading(true)
    try {
      await postAdminComplianceChecksCheckIdOverrideFailingCheck(
        checkId,
        {
          override_reason: reason.trim(),
          provider
        }
      )
      setReason('')
      reload()
    } catch (error: any) {
      console.error('Failed to override compliance check:', error)
      toast.error('Failed to override compliance check: ' + error.message)
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancel = () => {
    setReason('')
  }

  return (
    <>
      <DisplayTooltipOnHover text='Manually override failing status to passing'>
        <ShieldCheck
          size={16}
          strokeWidth={2}
          className='cursor-pointer text-blue-600 hover:text-blue-800'
          onClick={() => setIsModalOpen(true)}
        />
      </DisplayTooltipOnHover>

      <Modal open={isModalOpen} onOpenChange={setIsModalOpen}>
        <Modal.Body>
          <Modal.Title>Override Check Status</Modal.Title>
          <Modal.Description>
            Please provide a reason for manually overriding this failing check to passing status.
          </Modal.Description>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder='Enter reason for override...'
            className='w-full p-3 border border-gray-300 rounded-md resize-none h-24 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent mt-4'
          />
        </Modal.Body>
        <Modal.Footer>
          <div className='flex justify-end space-x-3'>
            <Button variant='secondary' onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              variant='primary'
              onClick={handleSubmit}
              disabled={!reason.trim() || isLoading}
            >
              {isLoading ? 'Overriding...' : 'Override'}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default OverrideEmployerCheckStatusButton
